// Loads common-ui before design-system: their barrels import each other and the reverse order leaves common-ui exports undefined.
import 'modules/common-ui';
import { useSentryFeatureFlags } from 'context/feature-flags/useSentryFeatureFlags';
import { useSentryUser } from 'modules/auth/hooks/useSentryUser';
import { NotificationContainer } from 'modules/common-ui/components/Notification';
import { Spinner, Tooltip } from 'modules/design-system';
import {
  AcceptInvitationPage,
  OAuthSignInPage,
  PasswordSignInPage,
  ResetPassword,
  SignInPage,
  SSOSignInPage,
} from 'modules/user';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';

import './sentry';

const Main = lazy(() =>
  import('./components/Main').then(({ Main }) => ({ default: Main })),
);
const PublicInspectionsRouter = lazy(() =>
  import('modules/inspections').then(({ PublicInspectionsRouter }) => ({
    default: PublicInspectionsRouter,
  })),
);
const SignupPages = lazy(() =>
  import('modules/signup').then(({ SignupPages }) => ({
    default: SignupPages,
  })),
);

export const reactRouterFutureFlags = {
  v7_relativeSplatPath: false,
  v7_fetcherPersist: false,
  v7_normalizeFormMethod: false,
  v7_partialHydration: false,
  v7_skipActionErrorRevalidation: false,
  v7_startTransition: false,
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <>
          <Outlet />
          <NotificationContainer />
          <Tooltip id="hiboo-tooltip" />
        </>
      ),
      children: [
        { index: true, element: <Main /> },
        { path: 'login', element: <SignInPage /> },
        { path: 'login/password', element: <PasswordSignInPage /> },
        { path: 'login/sso', element: <SSOSignInPage /> },
        { path: 'oauth/login', element: <OAuthSignInPage /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'signup/*', element: <SignupPages /> },
        { path: 'accept-invitation', element: <AcceptInvitationPage /> },
        { path: 'public/inspections/*', element: <PublicInspectionsRouter /> },
        { path: '*', element: <Main /> },
      ],
    },
  ],
  {
    future: reactRouterFutureFlags,
  },
);

export const RouterWrapper = () => {
  useSentryUser();
  useSentryFeatureFlags();

  return (
    <Suspense fallback={<Spinner />}>
      <RouterProvider
        router={router}
        fallbackElement={<Spinner />}
        future={reactRouterFutureFlags}
      />
    </Suspense>
  );
};
